import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useRef, useState } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import I18n from 'I18n';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import { H2 } from 'UIComponents/elements/headings';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIAlert from 'UIComponents/alert/UIAlert';
import { megabytesToBytes } from '../../../utils/units';
import { MAX_AVATAR_FILE_SIZE_MB } from '../../../consts';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { useUserByAuth } from '../../users/userQueries';
import AvatarCropper from './AvatarCropper';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISection from 'UIComponents/section/UISection';
import AvatarDropZone from './AvatarDropZone';

const AvatarUploadModal = ({
  onHideModal,
  onUploadAvatar
}) => {
  const {
    user
  } = useUserByAuth();
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarAlert, setAvatarAlert] = useState(null);
  const cropper = useRef();
  const dismissAvatarAlert = useCallback(() => {
    setAvatarAlert(null);
  }, []);
  const handleFileChange = useCallback(fileArrayBuffer => {
    dismissAvatarAlert();
    if (!fileArrayBuffer) return;

    if (fileArrayBuffer.byteLength > megabytesToBytes(MAX_AVATAR_FILE_SIZE_MB)) {
      setAvatarAlert({
        titleText: I18n.text('connectDotCom.avatarUpload.imageTooBig.title'),
        text: I18n.text('connectDotCom.avatarUpload.imageTooBig.text', {
          mbLimit: MAX_AVATAR_FILE_SIZE_MB
        })
      });
    } else {
      setAvatarPreview(fileArrayBuffer);
    }
  }, [dismissAvatarAlert]);
  const handleCloseModal = useCallback(() => {
    setAvatarPreview(null);
    onHideModal();
  }, [onHideModal]);
  const handleUpload = useCallback(() => {
    if (!avatarPreview) return;
    handleCloseModal();
    cropper.current.getCroppedImage().then(blob => {
      onUploadAvatar(blob);
    }).catch(() => {
      FloatingAlertStore.addAlert({
        message: I18n.text('connectDotCom.avatarUpload.uploadError'),
        type: 'danger'
      });
    });
  }, [avatarPreview, handleCloseModal, onUploadAvatar]);
  if (!user) return null;
  return /*#__PURE__*/_jsxs(UIModal, {
    size: "auto",
    width: 450,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: handleCloseModal
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "connectDotCom.avatarUpload.title"
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [avatarAlert && /*#__PURE__*/_jsx(UIAlert, {
        className: "m-bottom-8",
        closeable: true,
        onClose: dismissAvatarAlert,
        titleText: avatarAlert.titleText,
        type: "warning",
        children: avatarAlert.text
      }), /*#__PURE__*/_jsx(UIAlert, {
        type: "warning",
        className: "m-bottom-4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "connectDotCom.avatarUpload.usageWarning"
        })
      }), /*#__PURE__*/_jsx(UISection, {
        children: /*#__PURE__*/_jsx(AvatarDropZone, {
          onChange: handleFileChange
        })
      }), /*#__PURE__*/_jsx(UISection, {
        children: avatarPreview && /*#__PURE__*/_jsx(AvatarCropper, {
          url: avatarPreview,
          ref: cropper
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        disabled: !avatarPreview || Boolean(avatarAlert),
        onClick: handleUpload,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "connectDotCom.save"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: handleCloseModal,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "connectDotCom.cancel"
        })
      })]
    })]
  });
};

export default AvatarUploadModal;